<template>
    <div class="document-list-wrapper container-fluid p-t-25 p-r-0 p-l-0">
        <!-- ERROR MESSAGES -->
        <div class="raised low p-25 text-center" v-if="docTypes.length == 0" v-cloak>
            You do not have access to the documents on this page.
        </div>

        <div class="raised low p-25 text-center" v-else-if="errorMessage" v-cloak>
            {{ errorMessage }}
        </div>

        <div class="raised low p-25 text-center" v-else-if="accounts.length == 0 && user_is_sys_admin" v-cloak>
            System administrators must select an account to view documents
        </div>

        <div class="raised low p-25 text-center" v-else-if="docTypes.length > 1 && selectedDocTypeCodes.length == 0"
            v-cloak>
            Please select a document type to view documents
        </div>

        <!-- DOCUMENT LIST -->
        <div class="document-list-container" v-else :class="{ 'is-loading': is_retreiving_documents }">
            <div class="document-list-overlay" v-if="is_retreiving_documents">
                <i class="fa fa-spinner fa-spin fa-2x"></i>
            </div>
            <document-list v-show="!errorMessage" :documents="currentDocuments"
                :all_expanded="allExpanded" :document_type="document_type" :ev_outliers_enabled="ev_outliers_enabled"
                @toggle_selection="toggleSelection" @toggle_details="toggleDetails" @search="performSearch"
                @show_partial_results="showPartialResults" @change_view="goToGrid" @reset_page="resetPage"
                @go_to_old_portal="initiateOldPortalLink"></document-list>

            <paginator class="p-t-10 p-r-25 p-b-10 p-r-25" :available_page_sizes="[25, 50, 100, 250, 500]"
                :initial_page_size="documentsPerPage" :current_page="page" :current_page_size="documentsPerPage"
                :number_of_items="totalCount" :filtered_from="totalCount" :disabled="is_retreiving_documents"
                @change="changePagination"></paginator>
        </div>

        <!-- PARTIAL RESULTS MODAL -->
        <div ref="partialResultsModal" class="modal fade" role="dialog" tabindex="-1">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <i class="fa fa-close close" data-dismiss="modal" aria-label="Close" aria-hidden="true"></i>
                        <h4 class="modal-title" id="exampleModalLabel">Partial Results</h4>
                    </div>
                    <div class="modal-body" style="max-height:calc(100vh - 250px);overflow-y:auto">
                        <div class="modal-subheader" v-if="partialReportResultsFormatted.length > 0">
                            <i class="fa fa-info-circle modal-subheader-icon"></i>
                            <span class="text-bold">
                                These analytical results are preliminary and are subject to change pending final
                                approval
                            </span>
                        </div>
                        <div class="text-bold text-center p-25" v-if="loadingPartialResults">
                            Loading...
                        </div>
                        <transition-group name="fade" tag="div" v-else-if="partialReportResultsFormatted.length > 0">
                            <div v-for="(sample) in partialReportResultsFormatted" :key="sample.id">
                                <h3 class="m-l-25">Sample {{ sample.id }}</h3>
                                <table class="table table-condensed table-striped">
                                    <tbody>
                                        <tr v-for="item in sample.items">
                                            <td>{{ item.caption }}<span v-if="item.basis">&nbsp;({{ item.basis
                                            }})</span></td>
                                            <td>
                                                {{ item.value }}
                                                <span class="m-l-5"
                                                    v-if="item.unitsMatch === undefined || item.unitsMatch">{{
                                                        item.units }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </transition-group>
                        <div class="p-25" v-else>
                            Unable to retrieve results
                        </div>

                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-lg btn-secondary" data-dismiss="modal"><i
                                class="fa fa-times"></i> Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@import 'resources/assets/sass/global_variables';

.document-list-container {
    position: relative;

    &.is-loading {
        min-height: 150px;
    }

    .document-list-overlay {
        @include loading-overlay;
    }
}
</style>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

module.exports = {
    props: {
        user_is_sys_admin: { default: null },
        accounts: { default: function () { return [] } },
        report_finder: { default: function () { return { selected: false, items: [] } } },
        document_type: String,
        selectedPackages: Array,
        has_cc_hold: { type: Boolean, default: false },
        ev_outliers_enabled: { type: Boolean, default: false },
    },

    data: function () {
        return {
            account_nbrs: [],
            errorMessage: null,
            partialReportResults: [],
            loadingPartialResults: false,
            loadingReportDocs: false,
            old_portal_url: null,
            documentsPerPage: 25,
        }
    },

    computed: {
        ...mapState('documents', {
            docTypes: 'docTypes',
            documents: 'rows',
            startDate: 'startDate',
            endDate: 'endDate',
            needToSearch: 'needToSearch',
            selectedSampleTypes: 'selectedSampleTypes',
            selectAllChecked: 'selectAllChecked',
            outliersOnly: 'outliersOnly',
            projectPO: 'projectPO',
            showInProgress: 'showInProgress',
            page: 'page',
            totalCount: 'totalCount',
            is_retreiving_documents: 'isFetching',
            activeFilters: 'activeFilters',
            activeLikeFilters: 'activeLikeFilters',
        }),

        ...mapGetters('documents', {
            visibleDocuments: 'specificPageRows',
            filteredDocuments: 'visibleRows',
            selectedDocTypeCodes: 'selectedDocTypeCodes',
            selectedRows: 'selectedRows',
            hasActiveColumnFilters: 'hasActiveColumnFilters',
        }),

        currentDocuments: function () {
            return this.visibleDocuments();
        },

        partialReportResultsFormatted: function () {
            let samples = [];
            for (let key in this.partialReportResults) {
                if (!this.partialReportResults.hasOwnProperty(key)) continue;
                samples.push({ id: key, items: this.partialReportResults[key] });
            }
            return samples;
        },

        allExpanded() {
            return this.currentDocuments.filter(doc => doc.expandable && !doc.expanded).length == 0
        }
    },

    watch: {
        currentDocuments() {
            this.getReportDocuments();
        },
        ev_outliers_enabled(newVal) {
            if (!newVal && this.outliersOnly) {
                this.setOutliersOnly(false);
                this.searchUpdated(true);
                this.$emit('reset_page');
                this.$emit('search');
            }
        },
    },

    methods: {
        ...mapMutations('documents', [
            'SET_PAGINATION',
            'SET_TOTAL_COUNT',
        ]),

        ...mapActions('documents', [
            'updateRows', 'searchUpdated', 'selectRow', 'deselectRow', 'checkSelectAllButton',
            'changeStartDate', 'changeEndDate', 'setItemValue', 'updateSelectedDocTypeCodes',
            'fetchDocuments', 'setOutliersOnly', 'toggleInProgress',
            'fetchFilterValues', 'clearAllFilterValues',
        ]),

        resetPage() {
            this.SET_PAGINATION({ page: 1 });
        },

        initiateOldPortalLink: function (href) {
            if (href) {
                this.old_portal_url = null;
                const selected_accounts = this.accounts.map(account => { return account.account_nbr });
                href = href.replace(/\/$/, '');
                if (selected_accounts.length == 1) {
                    this.old_portal_url = href + '/' + selected_accounts[0];
                    this.goToOldPortal();
                }
                else {
                    this.old_portal_url = href;
                    this.$refs.oldPortalAccountPicker.showSelector();
                }
            }
        },

        goToOldPortal(account) {
            if (this.old_portal_url) {
                mwl.submitHiddenForm(this.old_portal_url, null, 'GET');
            }
        },

        goToGrid: function () {
            this.$emit('change_view', 'results');
        },

        queueSearch: function () {
            if (this.accounts.length > 0) {
                this.performSearch();
            }
        },

        performSearch: function () {
            if (!this.needToSearch) {
                return;
            }
            this.errorMessage = null;
            let docTypes = this.selectedDocTypeCodes;

            if (this.outliersOnly && !docTypes.includes('report')) {
                this.updateSelectedDocTypeCodes(['report']);
                docTypes = ['report'];
            }

            this.getDocuments(function (response) {
            }, function (response) {
                this.errorMessage = 'There was an error retrieving the documents.  You may need to narrow the date range on your search.';
            });
        },

        getDocuments: async function (success, error) {
            var excludeInProgress = !this.showInProgress;

            const allowEvFilters =
                this.ev_outliers_enabled &&
                this.selectedDocTypeCodes.includes('report');

            if (allowEvFilters && this.outliersOnly) {
                excludeInProgress = true;
            }

            var params = {
                docTypes: this.selectedDocTypeCodes,
                startDate: this.startDate,
                endDate: this.endDate,
                hasCcHold: this.has_cc_hold,
                excludeInProgress: excludeInProgress,
                outliersOnly: allowEvFilters ? this.outliersOnly : false,
                projectPO: this.projectPO || null,
            };

            if (this.report_finder.selected) {
                params.reportFinderItems = this.report_finder.items;
            }

            if (this.document_type === 'report' && !this.startDate && !this.endDate) {
                var momentFormat = 'YYYY-MM-DD';
                var today = moment();
                this.changeEndDate(today.format(momentFormat));
            }

            params.accountNbrs = [];
            for (var i = 0; i < this.accounts.length; i++) {
                params.accountNbrs.push(this.accounts[i].account_nbr);
            }

            if (this.selectedSampleTypes.length > 0) {
                params.sampleTypes = this.selectedSampleTypes;
            }

            if (this.selectedPackages.length > 0) {
                params.projectIDs = this.selectedPackages.map(function (pck) { return pck.id; });
            }

            this.SET_PAGINATION({ page: 1, per_page: this.documentsPerPage });
            this.SET_TOTAL_COUNT(0);
            this.clearAllFilterValues();

            try {
                const [data] = await Promise.all([
                    this.fetchDocuments(params),
                    this.fetchFilterValues({ params }),
                ]);
                if (data === undefined) return;
                if (this.report_finder.selected && (data.documents || []).length > 5) {
                    this.errorMessage = "This function was designed to help find a specific report.  Your search resulted in more than 5 matches.  Please refine your search and try again.";
                }
                if (data.startDate && data.startDate != this.startDate) {
                    const notify = !!params.startDate && data.startDate > params.startDate;
                    this.changeStartDate(data.startDate);
                    if (notify) {
                        swal('The start date has been modified because your search resulted in too many records.');
                    }
                } else if (data.endDate && data.endDate != this.endDate) {
                    const notify = !!params.endDate && data.endDate < params.endDate;
                    this.changeEndDate(data.endDate);
                    if (notify) {
                        swal('The end date has been modified because your search resulted in too many records.');
                    }
                }
                this.searchUpdated(false);
                if (typeof success === 'function') success.call(this, data);
            } catch (e) {
                this.updateRows({ rows: [], filter_data: [] });
                if (typeof error === 'function') error.call(this, e);
            }
        },

        getReportDocuments: function (success, retries = 0) {
            if (this.loadingReportDocs) return

            let pendingDocs = this.currentDocuments.filter(document =>
                document.type.code == 'report' && !document.documentsRetrieved && (!document.versions || document.versions.length == 0)
            );
            let reports = pendingDocs.map(document => {
                if (document.id.label) return document.id.label;
                if (document.id.number) return document.id.number;
                return false;
            });
            if (reports.length) {
                this.loadingReportDocs = true;
                let request = this.$http.post('/documents/reports/documents', { reports: reports });
                request.then(function (response) {
                    if (response.data) {
                        response.data.documents.versions = response.data.documents.reports;
                        let itemTypes = ['versions', 'orderforms', 'billsummaries'];
                        let reportDocuments = {};
                        itemTypes.forEach(function (itemType) {
                            let items = (response.data.documents[itemType]) ? response.data.documents[itemType] : [];
                            for (let i = 0; i < items.length; i++) {
                                if (!reportDocuments[items[i].reportNumber]) reportDocuments[items[i].reportNumber] = {};
                                reportDocuments[items[i].reportNumber][itemType] = items[i].versions;
                            }
                        });
                        for (let i = 0; i < this.currentDocuments.length; i++) {
                            let doc = this.currentDocuments[i];
                            let reportNumber = (doc.id.label) ? doc.id.label : doc.id.number;
                            if (reportDocuments[reportNumber]) {
                                doc.versions = reportDocuments[reportNumber].versions ? reportDocuments[reportNumber].versions : [];
                                doc.orderforms = reportDocuments[reportNumber].orderforms ? reportDocuments[reportNumber].orderforms : [];
                                doc.billsummaries = reportDocuments[reportNumber].billsummaries ? reportDocuments[reportNumber].billsummaries : [];
                                doc.data.report_btn = doc.versions;
                                doc.data.order_form_btn = doc.orderforms;
                                doc.data.billing_summary_btn = doc.billsummaries;
                            }
                            this.setItemValue({
                                item: this.currentDocuments[i],
                                key: 'documentsRetrieved',
                                value: true
                            })
                        }
                    }
                    this.loadingReportDocs = false;
                    if (typeof success === 'function') success(response);
                }).catch(() => {
                    this.loadingReportDocs = false;
                    if (retries === 0) {
                        setTimeout(() => this.getReportDocuments(success, retries + 1), 3000);
                    } else {
                        pendingDocs.forEach(doc => this.setItemValue({ item: doc, key: 'docRetrievalFailed', value: true }));
                        toastr.error('Unable to load PDF links. Please refresh the page and try again.', 'Error!');
                    }
                });
            }
        },

        showPartialResults: function (id) {
            this.partialReportResults = [];
            this.loadingPartialResults = true;
            this.$http.post('/results/reports', { report_ids: [id] })
                .then((response) => {
                    if (response.data) {
                        this.partialReportResults = response.data;
                        this.loadingPartialResults = false;
                    }
                });
            $(this.$refs.partialResultsModal).modal('show');
        },

        changePagination: function (pageNumber, itemsPerPage) {
            this.documentsPerPage = itemsPerPage;
            this.SET_PAGINATION({ page: pageNumber, per_page: itemsPerPage });
            this.fetchDocuments().catch(() => {
                this.errorMessage = 'There was an error retrieving the documents. Please try again.';
            });
        },

        toggleSelection: function (index) {
            if (index === undefined) {
                if (this.selectAllChecked) {
                    this.deselectAll();
                }
                else {
                    this.selectAll();
                }
            }
            else {
                let item = _.has(this.currentDocuments, index) ? this.currentDocuments[index] : {};
                if (item.selected) {
                    this.deselectItem(index);
                }
                else {
                    this.selectItem(index);
                }
            }
        },

        toggleDetails(index) {
            if (index === undefined) {
                let val = !this.allExpanded;
                for (let i = 0; i < this.currentDocuments.length; i++) {
                    if (this.currentDocuments[i].expandable) {
                        this.setItemValue({
                            item: this.currentDocuments[i],
                            key: 'expanded',
                            value: val
                        })
                    }
                }
            }
            else {
                let item = _.has(this.currentDocuments, index) ? this.currentDocuments[index] : {};
                if (item.expandable) {
                    this.setItemValue({
                        item,
                        key: 'expanded',
                        value: !item.expanded
                    })
                }
            }
        },

        selectItem: function (index) {
            let item = _.has(this.currentDocuments, index) ? this.currentDocuments[index] : {};

            if (!item.selectable) return;

            let document = this.currentDocuments[index];

            if (this.document_type === 'invoice') {
                const invoice_type = _.has(document, ['data', 'invoice_type']) ? document.data.invoice_type : null
                let current_selection_invoice_type = this.selectedRows.map(row => {
                    return _.has(row, ['data', 'invoice_type']) ? row.data.invoice_type : null
                })
                current_selection_invoice_type = current_selection_invoice_type[0] || null;

                if (current_selection_invoice_type && current_selection_invoice_type != invoice_type) {
                    swal('', 'Monthly invoices and individual reports can not be paid together', 'info')
                    return
                }
            }

            this.selectRow(index);

            this.updateSelectAllCheckbox();
        },

        selectAll: function () {
            for (let i = 0; i < this.currentDocuments.length; i++) {
                const document = this.currentDocuments[i];
                if (document.selectable) {
                    this.selectRow(i);
                }
            }
            this.updateSelectAllCheckbox();
        },

        deselectItem: function (index) {
            this.deselectRow(index);
            this.updateSelectAllCheckbox();
        },

        deselectAll: function () {
            for (let i = 0; i < this.currentDocuments.length; i++) {
                const document = this.currentDocuments[i];
                if (document.selectable) {
                    this.deselectRow(i);
                }
            }
            this.updateSelectAllCheckbox();
        },

        updateSelectAllCheckbox: function () {
            const selectables = this.currentDocuments.filter(doc => doc.selectable);
            // 'every' returns true if the array is empty
            this.checkSelectAllButton(selectables.length > 0 && selectables.every(doc => doc.selected));
        }
    }
}
</script>



// WEBPACK FOOTER //
// document-list-standard-view.vue?3ebe56e9